import { List, Typography } from 'antd';
import { Avatar, InputSearchV2 } from 'components/common';
import { TreeSelectOrganizationUnits } from 'components/organization-units';
import { useProfile, useUsersOptions } from 'hooks';
import { usersMessages } from 'messages';
import VirtualList from 'rc-virtual-list';
import { ReactNode, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';
import { UserCompactDto } from 'types';

type UserListProps = {
  onClickItem?: (value: UserCompactDto) => void;
  className?: string;
  renderItem?: (item: UserCompactDto) => ReactNode;
  placeholder?: string;
  filterOrganizationUnit?: boolean;
  allOrgUnderRoot?: boolean;
};

const UserList: React.FC<UserListProps> = ({
  onClickItem,
  className,
  renderItem,
  placeholder,
  filterOrganizationUnit,
  allOrgUnderRoot
}) => {
  const { profile } = useProfile();

  const [keyword, setKeyword] = useState('');
  const [organizationUnitId, setOrganizationUnitId] = useState(profile?.organizationUnit?.organizationUnitId);

  const { usersOptions, handleLoadMore, isLoading, resetPage, hasMore } = useUsersOptions({
    keyword,
    organizationUnitId: organizationUnitId ? [organizationUnitId] : undefined,
    isIgnoreUserOrganizationUnitId: filterOrganizationUnit ? true : false
  });

  const handleSearch = (val: string) => {
    resetPage();
    setKeyword(val);
  };

  const onScroll = (e: React.UIEvent<HTMLElement, UIEvent>) => {
    if (Math.abs(e.currentTarget.scrollHeight - e.currentTarget.scrollTop) <= 1) {
      handleLoadMore();
    }
  };

  return (
    <div className={twMerge('flex max-h-full flex-col px-6 pt-5', className)}>
      <InputSearchV2
        className='mb-2'
        placeholder={placeholder ?? usersMessages.fullName}
        onChange={(e) => handleSearch(e.toString())}
      />
      {filterOrganizationUnit ? (
        <div className='mb-2'>
          <TreeSelectOrganizationUnits
            placeholder={usersMessages.organizationUnit}
            popupMatchSelectWidth={520}
            treeCheckable={false}
            labelInValue
            showCheckedStrategy='SHOW_ALL'
            treeDefaultExpandAll
            value={organizationUnitId}
            onSelect={setOrganizationUnitId}
            showSearch
            allOrgUnderRoot={allOrgUnderRoot}
          />
        </div>
      ) : undefined}
      <List loading={isLoading} className='-mx-6 max-h-[calc(100%-48px)] overflow-auto pb-6'>
        <VirtualList itemKey='userId' data={usersOptions} fullHeight>
          {(item: UserCompactDto) =>
            renderItem ? (
              renderItem(item)
            ) : (
              <List.Item
                key={item.userId}
                className='cursor-pointer px-5 hover:bg-colorBgIconHover'
                onClick={() => onClickItem?.(item)}
              >
                <List.Item.Meta
                  avatar={<Avatar size={48} fileId={item.avatar} name={item.fullName} />}
                  title={
                    <Typography.Title level={5} className='m-0'>
                      {item.fullName}
                    </Typography.Title>
                  }
                  description={item.fullName}
                />
              </List.Item>
            )
          }
        </VirtualList>
        {!isLoading && hasMore && (
          <InView
            onChange={(inView) => {
              if (inView) {
                handleLoadMore();
              }
            }}
            className='flex items-center gap-4 px-5'
          >
            <div className='skeleton-active h-12 w-12 rounded-full'></div>

            <div className='skeleton-active h-5 w-40 rounded-sm'></div>
          </InView>
        )}
      </List>
    </div>
  );
};

export default UserList;
